import {FC, useEffect, useRef, useState} from 'react'
import {shallowEqual, useSelector} from 'react-redux'
import {useHistory} from 'react-router-dom'
import {RootState} from '../../../setup'
import {useLayout} from '../../../_metronic/layout/core'
import {UserRole} from '../../constants/roles'
import {showErrorAlert} from '../../helpers/alert'
import {UserModel} from '../../modules/auth/models/UserModel'
import {hasRole} from '../../utils/permissions'
import {formatDateStrShort} from '../../helpers/FormatDate'
import Pagination from '../components/Pagination'
import {getSuggestions} from '../../services/suggestionService'
import {SUGGESTION_STATUSES, statusColor, statusLabel} from './suggestions'
import {SuggestionDetailModal} from './components/SuggestionDetailModal'

const LIMIT = 10

// E1 staff queue: Suggest-to-Nobilis submissions with a status filter + search, a detail view and
// status-transition / convert actions. BLOCKED until the E1-backend endpoints deploy — a failed
// fetch shows a "not available yet" note instead of crashing.
const SuggestionsAdminPage: FC = () => {
  const history = useHistory()
  const user = useSelector<RootState>(({auth}) => auth.user, shallowEqual) as UserModel
  const {config, setLayout} = useLayout()
  const restoreRef = useRef(config)

  const [status, setStatus] = useState('')
  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')
  const [page, setPage] = useState(1)
  const [rows, setRows] = useState<any[]>([])
  const [count, setCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [selected, setSelected] = useState<any | null>(null)

  const totalPages = count ? Math.ceil(count / LIMIT) : 1

  useEffect(() => {
    restoreRef.current = config
    setLayout({
      ...config,
      header: {...config.header, display: true},
      aside: {...config.aside, display: true},
      toolbar: {...config.toolbar, display: false},
      footer: {...config.footer, display: true},
    })
    if (!hasRole(user, UserRole.ADMIN)) {
      history.goBack()
      showErrorAlert({
        title: 'Access Denied',
        message: 'You do not have permission to view this page.',
      })
    }
    return () => setLayout(restoreRef.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Debounce the search box.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300)
    return () => clearTimeout(t)
  }, [search])

  // Reset to page 1 when the filter/search changes.
  useEffect(() => {
    setPage(1)
  }, [status, debounced])

  const load = async () => {
    try {
      setLoading(true)
      setError(false)
      const data = await getSuggestions({
        status: status || undefined,
        search: debounced || undefined,
        limit: LIMIT,
        offset: (page - 1) * LIMIT,
      })
      setRows(data?.results ?? [])
      setCount(Number(data?.count ?? 0))
    } catch (err) {
      setError(true)
      setRows([])
      setCount(0)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, debounced, page])

  return (
    <div className='admin-community__wrapper'>
      <h3>Suggested Experiences</h3>

      <div className='admin-community__list_wrap'>
        <div className='d-flex flex-wrap align-items-center justify-content-between gap-3 mb-5'>
          <input
            type='text'
            className='form-control w-auto'
            placeholder='Search suggestions'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select
            className='form-select w-auto'
            aria-label='Filter by status'
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value=''>All statuses</option>
            {SUGGESTION_STATUSES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>

        {error ? (
          <div className='text-center py-10 text-muted'>
            The suggestions service is not available yet. This screen is blocked on the E1-backend
            deploy.
          </div>
        ) : !loading && rows.length === 0 ? (
          <div className='text-center py-10'>No suggestions found.</div>
        ) : (
          <table className='admin-community__table'>
            <thead>
              <tr>
                <th>Title</th>
                <th>Submitted by</th>
                <th>Category</th>
                <th>Status</th>
                <th>Received</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td>{r.title || '—'}</td>
                  <td>{r.submittedBy?.name || r.submittedBy?.email || '—'}</td>
                  <td>{r.category || '—'}</td>
                  <td>
                    <span
                      className='badge'
                      style={{backgroundColor: statusColor(r.status), color: '#fff'}}
                    >
                      {r.statusDisplay || statusLabel(r.status)}
                    </span>
                  </td>
                  <td>{r.createdAt ? formatDateStrShort(r.createdAt) : '—'}</td>
                  <td>
                    <button
                      type='button'
                      className='nb-link-underline-500'
                      onClick={() => setSelected(r)}
                    >
                      Review
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {!error && rows.length > 0 && (
          <div className='tap-flex-center mt-5'>
            <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
          </div>
        )}
      </div>

      <SuggestionDetailModal
        suggestion={selected}
        onClose={() => setSelected(null)}
        onChanged={load}
      />
    </div>
  )
}

export default SuggestionsAdminPage
